// STEP 3-SHIP-E · PHASE D-2 · Bridge: LIVEMOA Seller SPA ↔ Windows Waybill Agent.
// Reuses D-1 Agent HTTP contract as-is. Never touches:
//   • carrierProfiles / CarrierExportModal internal flow
//   • BulkInvoice / Combined shipment / Inventory
// Agent = optional enhancement · CSV Download stays as LOCK fallback.

const AGENT_BASE = 'http://127.0.0.1:53277';
const TOKEN_KEY = 'livemoa.agent.session_token';       // isolated from LIVEMOA login token
const HEALTH_TIMEOUT_MS = 1500;
const DEFAULT_TIMEOUT_MS = 8000;

/** Storage helpers — token is not a LIVEMOA credential; it's only the Agent bridge token. */
const _getToken = () => {
  try { return window.localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
};
const _setToken = (t) => {
  try { window.localStorage.setItem(TOKEN_KEY, t); } catch {}
};
const _clearToken = () => {
  try { window.localStorage.removeItem(TOKEN_KEY); } catch {}
};

/** Normalised error codes surface a friendly Korean message + machine code. */
const _err = (code, message, cause) => Object.assign(new Error(message), { code, cause });

const _abortable = (ms) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return { signal: ctrl.signal, clear: () => clearTimeout(t) };
};

const _request = async (path, { method = 'GET', body, auth = false, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) => {
  const { signal, clear } = _abortable(timeoutMs);
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const tok = _getToken();
    if (!tok) throw _err('UNPAIRED', 'Agent 페어링이 필요합니다.');
    headers.Authorization = `Bearer ${tok}`;
  }
  try {
    const res = await fetch(`${AGENT_BASE}${path}`, {
      method,
      headers,
      body: method === 'GET' ? undefined : (body ? JSON.stringify(body) : undefined),
      credentials: 'omit',
      mode: 'cors',
      signal,
    });
    let json = null;
    try { json = await res.json(); } catch { /* body may be empty */ }
    if (!res.ok) {
      if (res.status === 401) throw _err('UNAUTHORIZED', json?.detail || 'Agent 인증 실패', json);
      if (res.status === 403) throw _err('ORIGIN_BLOCKED', json?.detail || 'Agent Origin 차단', json);
      if (res.status === 404) throw _err('NOT_FOUND', json?.detail || 'Not Found', json);
      throw _err(`HTTP_${res.status}`, json?.detail || `HTTP ${res.status}`, json);
    }
    return json;
  } catch (e) {
    if (e && e.name === 'AbortError') throw _err('TIMEOUT', 'Agent 응답 시간 초과');
    if (e && e.code) throw e;
    // TypeError on fetch → offline / CORS blocked / not installed
    throw _err('AGENT_OFFLINE', 'LIVEMOA 송장 Agent가 실행되지 않았습니다.', e);
  } finally {
    clear();
  }
};

/** Non-throwing health probe. Returns `{available, paired?, version?, error?}`. */
export const checkAgent = async () => {
  try {
    const h = await _request('/health', { timeoutMs: HEALTH_TIMEOUT_MS });
    return { available: true, paired: !!h.paired, version: h.version, agent: h.agent };
  } catch (e) {
    return { available: false, error: e.code, message: e.message };
  }
};

/** Non-throwing pairing status probe. */
export const getPairingStatus = async () => {
  try {
    const s = await _request('/pair/status', { timeoutMs: HEALTH_TIMEOUT_MS });
    return { available: true, paired: !!s.paired, state: s.state };
  } catch (e) {
    return { available: false, paired: false, error: e.code };
  }
};

/** Begin pairing — returns `{pairing_code, session_token, expires_at}`. Token is NOT
 *  persisted until `confirmPairing()` succeeds so a failed confirm cannot poison state. */
export const startPairing = async () => {
  return _request('/pair/start', { method: 'POST' });
};

/** Confirm the pairing using the code+token from `startPairing()`. On success the
 *  session token is persisted in localStorage under a namespace isolated from LIVEMOA login. */
export const confirmPairing = async ({ pairing_code, session_token }) => {
  const res = await _request('/pair/confirm', {
    method: 'POST',
    body: { pairing_code, session_token },
  });
  _setToken(session_token);
  return res;
};

/** Reset local + remote pairing. Safe to call when Agent is offline (local cleared regardless). */
export const resetPairing = async () => {
  _clearToken();
  try { await _request('/pair/reset', { method: 'POST' }); } catch { /* offline is fine */ }
  return { paired: false };
};

/** Push a job (idempotent by job_id).
 *  D-3: when `carrier_rows` is provided the Agent will materialize an
 *  `input.csv` in its local job folder. Idempotent via `rows_hash`. */
export const pushJob = async ({ job_id, carrier, carrier_rows, rows_hash }) => {
  if (!job_id || !carrier) throw _err('BAD_REQUEST', 'job_id 와 carrier 는 필수입니다.');
  const body = { job_id, carrier };
  if (carrier_rows) body.carrier_rows = carrier_rows;
  if (rows_hash) body.rows_hash = rows_hash;
  return _request('/jobs', { method: 'POST', body, auth: true });
};

/** Fetch a single job status. */
export const getJob = async (jobId) => {
  return _request(`/jobs/${encodeURIComponent(jobId)}`, { auth: true });
};

/** D-3 · deterministic SHA-256 over `{carrier, rows}` — canonical JSON with
 *  sorted keys / no whitespace / raw unicode. Bytes MUST match the Agent's
 *  Python `compute_rows_hash()` output exactly.
 *  Requires a secure context (HTTPS / localhost) for `crypto.subtle`. */
const _canonicalize = (obj) => {
  if (obj === null || typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) return '[' + obj.map(_canonicalize).join(',') + ']';
  const keys = Object.keys(obj).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + _canonicalize(obj[k])).join(',') + '}';
};

export const computeRowsHash = async (carrier, rows) => {
  const canon = _canonicalize({ carrier, rows });
  const bytes = new TextEncoder().encode(canon);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

/** Clear only the local token (does not talk to Agent). Used at LIVEMOA logout. */
export const forgetAgentToken = () => _clearToken();

// ── D-4/D-5 · Result folder watch + tracking auto-import ──────────────
/** Get the current watched folder configuration. */
export const getWatchConfig = async () => _request('/watch/config', { auth: true });

/** Update the watched folder. Pass `null` to disable watching. */
export const setWatchConfig = async (folder) => _request('/watch/config', {
  method: 'POST', body: { folder }, auth: true,
});

/** Non-throwing status probe for the CarrierExportModal status strip. */
export const getWatchStatus = async () => {
  try { return { ok: true, ...(await _request('/watch/status', { auth: true })) }; }
  catch (e) { return { ok: false, error: e.code, message: e.message }; }
};

/** Fetch parsed but not-yet-applied tracking rows produced by the watcher. */
export const getPendingResults = async (limit = 500) =>
  _request(`/results/pending?limit=${encodeURIComponent(limit)}`, { auth: true });

/** Acknowledge parsed rows (APPLIED/SKIPPED/FAILED) so the Agent stops
 *  re-offering them and updates its file-level aggregates. */
export const ackResults = async (acks) => _request('/results/ack', {
  method: 'POST', body: { acks }, auth: true,
});

// ── D-6 · Shell helpers (SAFE · no CJ login automation) ────────────────
/** Open an https URL in the user's default Windows browser via the Agent.
 *  Server enforces HTTPS + cjlogistics.com host allowlist. */
export const shellOpenUrl = async (url) => _request('/shell/open-url', {
  method: 'POST', body: { url }, auth: true,
});

/** Reveal the just-produced CJ input.csv in Windows Explorer (selects the
 *  file so the user can drag-drop / press [열기] with a single motion). */
export const shellRevealJobFile = async (jobId) => _request('/shell/reveal-file', {
  method: 'POST', body: { job_id: jobId }, auth: true,
});

// CJ LOIS Parcel · 기업고객 진입 URL. REAL WINDOWS TEST HOTFIX-2 (2026-02-04):
// 이전 값 `parcel.cjlogistics.com/…/fileReceipt.do` 는 DNS_PROBE_FINISHED_NXDOMAIN.
// 현행 실사용 도메인은 `loisparcelp.cjlogistics.com` (CJ LOIS Parcel · CNPLUS 후속).
// deep-link path 는 사용자 실측 확정 전까지 신설하지 않고 검증된 진입 URL 만 사용.
export const CJ_LOIS_FILE_RECEIPT_URL =
  'https://loisparcelp.cjlogistics.com/index.do';

// ── D-6D-1 · CJ automation orchestration (thin wrappers · no new endpoints) ──
/** Kick off `/cj/automate/{jobId}/start`. Response includes `driver_attached`
 *  which the frontend uses to decide between D-6D real automation and D-6C
 *  fallback (shellRevealJobFile + shellOpenUrl). Payload minimal — server
 *  reads job_id from URL AND body for cross-check. */
export const startCjAutomation = async (jobId, expectedShipments) => {
  const body = { job_id: jobId };
  if (typeof expectedShipments === 'number') body.expected_shipments = expectedShipments;
  return _request(`/cj/automate/${encodeURIComponent(jobId)}/start`, {
    method: 'POST', body, auth: true,
  });
};

/** Non-throwing status probe used by the polling loop. Returns null on
 *  404 / offline so the UI can drop back to D-6C helper text gracefully. */
export const getCjAutomationStatus = async (jobId) => {
  try {
    return await _request(`/cj/automate/${encodeURIComponent(jobId)}/status`,
                          { auth: true, timeoutMs: HEALTH_TIMEOUT_MS });
  } catch (e) {
    if (e.code === 'NOT_FOUND') return null;
    throw e;
  }
};

/** Cancel the active CJ automation session. Idempotent server-side. */
export const cancelCjAutomation = async (jobId, reason = 'user cancel') => {
  return _request(`/cj/automate/${encodeURIComponent(jobId)}/cancel`, {
    method: 'POST', body: { reason }, auth: true,
  });
};

// ── D-6E-FINAL GAP-2 · CJ result Excel auto-retrieval (seller-triggered) ──
/** List Agent jobs (all carriers).  Returns `{jobs: [...]}` sorted by
 *  `created_at DESC` (see store.list_jobs).  Used by the ⑥ 송장등록 button
 *  to resolve the most recent CJ job id — no persistence, no LIVEMOA
 *  credential handling. */
export const listAgentJobs = async () => _request('/jobs', { auth: true });

/** Trigger the Agent to re-open a NEW Chrome context on the dedicated
 *  cj_profile, wait for the result grid, click [엑셀] once, and drop the
 *  file into the seller's configured watch folder so the existing watcher
 *  → parser → bulk-invoice pipeline auto-registers the tracking numbers.
 *
 *  Preconditions (판매자 지시 D-6E FINAL §7/§8):
 *   - Seller has completed 접수확정 → 운송장출력 → 대상 선택 → 운송장출력
 *     in the currently-open Agent Chrome and CJ has generated tracking
 *     numbers.
 *   - Seller has CLOSED the Agent Chrome window (SingletonLock release);
 *     otherwise Agent returns 409 CJ_PROFILE_LOCKED with an actionable
 *     message and the caller retries after seller confirms.
 *
 *  Idempotent: repeat clicks re-attempt the same download; watcher/parser
 *  dedup by SHA-256(file bytes) so no duplicate tracking rows are
 *  produced. */
export const cjResultDownload = async (jobId, waitSeconds = 90) => {
  if (!jobId) throw _err('BAD_REQUEST', 'CJ job_id 가 필요합니다.');
  const q = Number.isFinite(waitSeconds) ? `?wait_seconds=${encodeURIComponent(waitSeconds)}` : '';
  return _request(`/cj/result-download/${encodeURIComponent(jobId)}${q}`, {
    method: 'POST', body: {}, auth: true,
    // The endpoint may block up to `waitSeconds` while polling for the
    // result grid.  Give the fetch a generous ceiling above that.
    timeoutMs: Math.max(120_000, (Number(waitSeconds) || 90) * 1000 + 30_000),
  });
};

/** Test-only helper — never call from prod code paths. */
export const _AGENT_BASE = AGENT_BASE;
